import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { map, tap, withLatestFrom, catchError, switchMap, concatMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';

import * as ConsultationActions from './consultation.actions';
import * as ConsultationSelectors from './consultation.selectors';
import * as fromConsultation from './consultation.reducer';
import { APIService } from '../../services/api/api.service';
import { Store } from '@ngrx/store';
import { ListQueryService } from '../../components/common/pagination/list-query.service';
import { listInvalidationEffect } from '../../components/common/pagination/list-query-invalidation.effect';
import { CONSULTATION_LIST_QUERY_PREFIX } from './consultation.query-keys';

@Injectable()
export class ConsultationEffects {
  // A consultation mutation can change what any list page shows, so invalidate
  // the keyed list caches the list page renders from. The embedded cursor lists
  // are unaffected — they read the NgRx collection.
  invalidateListQueries$ = listInvalidationEffect(
    () => this.actions$,
    () => this.listQuery,
    CONSULTATION_LIST_QUERY_PREFIX,
    [
      ConsultationActions.saveConsultationSuccess,
      ConsultationActions.updateConsultationSuccess,
      ConsultationActions.clearNotification
    ]
  );

  loadConsultations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.loadConsultations),
      concatMap(action =>
        of(action).pipe(
          withLatestFrom(
            this.store.select(ConsultationSelectors.getConsultationState)
          )
        )
      ),
      switchMap(([action, state]) => {
        const filters = { ...action.filters };
        if (action.loadType === 'newer' && state.maxId) {
          Object.assign(filters, { minId: state.maxId });
        } else if (action.loadType === 'older' && state.minId) {
          Object.assign(filters, { maxId: state.minId });
        }

        return this.apiService.getConsultations(filters).pipe(
          // Cursor calls omit includeCount, so the API returns a bare array;
          // normalise in case a paginated envelope ever comes back.
          map(res => (Array.isArray(res) ? res : res.data)),
          tap(consultations => {
            if (!consultations.length) {
              if (action.loadType === 'newer') {
                this.notification.info('Info', 'You are all caught up!');
              } else {
                this.notification.info('Info', 'There are no older consultations');
              }
            }
          }),
          map(consultations =>
            ConsultationActions.loadConsultationsSuccess({
              consultations,
              loadType: action.loadType
            })
          ),
          catchError(error => {
            console.error('Error', error);
            this.notification.error('Error', 'There was an error loading consultations.');
            return of(ConsultationActions.loadConsultationsFailure({ error }));
          })
        );
      })
    )
  );

  saveConsultationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ConsultationActions.saveConsultationSuccess),
        tap(({ consultation }) =>
          this.notification.success('Successfully saved.', '')
        )
      ),
    { dispatch: false }
  );

  saveConsultationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ConsultationActions.saveConsultationFailure),
        tap(({ error }) => {
          console.error('saveConsultationFailure', error);

          this.notification.error(
            'Error',
            'There was an error saving consultation.'
          );
        })
      ),
    { dispatch: false }
  );

  updateConsultation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.updateConsultation),
      tap(() => this.notification.success('Consultation updating...', '')),
      switchMap(action =>
        this.apiService
          .updateConsultation(action.id, action.consultation)
          .pipe(
            map(consultation =>
              ConsultationActions.updateConsultationSuccess({ consultation, callback: action.callback })
            ),
            catchError(error => {
              console.error('Error', error);
              this.notification.error('Error', 'There was an error updating consultation.');
              return of(ConsultationActions.updateConsultationFailure({ error }));
            })
          )
      )
    )
  );

  updateConsultationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConsultationActions.updateConsultationSuccess),
      tap((action) => {
        this.notification.success('Successfully updated.', '')
        if (action.callback) {
          action.callback();
        }
      })
    ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private apiService: APIService,
    private notification: NzNotificationService,
    private store: Store<fromConsultation.ConsultationPartialState>,
    private listQuery: ListQueryService
  ) {}
}
